import axios from 'axios';


export const generateWithOpenRouter = async (messages) => {
  try {
    console.log('Attempting OpenRouter request...');
    const apiKey = import.meta.env.VITE_OPENROUTER_API_KEY;
    const apiUrl = import.meta.env.VITE_OPENROUTER_URL;
    console.log('Using API key:', apiKey ? 'Present' : 'Missing');
    console.log('Using API URL:', apiUrl);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'HTTP-Referer': window.location.origin,
        'X-Title': 'IX-Study',
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        model: "liquid/lfm-2.5-1.2b-instruct:free",
        messages: messages.map(msg => ({
          role: msg.role,
          content: msg.content
        }))
      })
    });

    // Parse JSON response
    if (!response.ok) {
      let errorData;
      try {
        const raw = await response.text();
        errorData = JSON.parse(raw);
        const errorMessage = errorData.error?.message || errorData.error?.code || response.statusText;
        console.error('OpenRouter error response:', errorData);
        throw new Error(`OpenRouter API error: ${errorMessage}`);
      } catch (e) {
        // If it's already our error, re-throw it
        if (e.message && e.message.startsWith('OpenRouter API error:')) {
          throw e;
        }
        // Otherwise, it's a JSON parse error or network error
        console.error('OpenRouter error response:', errorData || await response.text());
        throw new Error(`OpenRouter API error: ${response.statusText || 'Unknown error'}`);
      }
    }
    
    // Parse successful JSON response
    let data;
    try {
      data = await response.json(); // Use response.json() instead of text() + JSON.parse()
    } catch (e) {
      console.error('OpenRouter JSON parse error:', e);
      const rawText = await response.text();
      console.error('OpenRouter raw response:', rawText);
      throw new Error('OpenRouter returned invalid JSON: ' + rawText.substring(0, 200));
    }
    
    if (!data?.choices?.[0]?.message?.content) {
      console.error('OpenRouter response structure:', data);
      throw new Error('Invalid or empty response from OpenRouter: ' + JSON.stringify(data));
    }

    return data.choices[0].message.content;
  } catch (error) {
    console.error('OpenRouter error details:', {
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

// If running in Node.js, use groq-sdk for streaming. In browsers/React, streaming is not fully supported.
import { Groq } from 'groq-sdk';

export const generateWithGroq = async (messages, options = {}) => {
  const apiKey = import.meta.env.VITE_GROQ_API_KEY;
  if (!apiKey) {
    throw new Error('Groq API key is not set. Please check your environment variables (VITE_GROQ_API_KEY).');
  }
  
  // Trim API key to remove any accidental whitespace
  const trimmedApiKey = apiKey.trim();
  
  // Try to use groq-sdk (works in Node.js and may work in browser if bundled)
  try {
    const groq = new Groq({ apiKey: trimmedApiKey });
    const chatCompletion = await groq.chat.completions.create({
      model: options.model || "meta-llama/llama-4-scout-17b-16e-instruct",
      messages: messages.map(msg => ({
        role: msg.role,
        content: msg.content
      })),
      temperature: options.temperature ?? 1,
      max_completion_tokens: options.max_completion_tokens ?? 1024,
      top_p: options.top_p ?? 1,
      stream: options.stream ?? true,
      stop: options.stop ?? null
    });
    
    // Handle streaming response
    if (options.stream !== false) {
      let result = '';
      for await (const chunk of chatCompletion) {
        result += chunk.choices[0]?.delta?.content || '';
      }
      return result;
    } else {
      // Non-streaming response
      return chatCompletion.choices[0]?.message?.content || '';
    }
  } catch (sdkError) {
    // If groq-sdk fails (e.g., in browser), fall back to fetch API
    console.warn('Groq SDK not available, falling back to fetch API:', sdkError);
    
    const apiUrl = import.meta.env.VITE_GROQ_API_URL || 'https://api.groq.com/openai/v1/chat/completions';
    
    // For fetch API, always use non-streaming to get JSON response
    const requestBody = {
      model: options.model || "meta-llama/llama-4-scout-17b-16e-instruct",
      messages: messages.map(msg => ({
        role: msg.role,
        content: msg.content
      })),
      temperature: options.temperature ?? 1,
      max_completion_tokens: options.max_completion_tokens ?? 1024,
      top_p: options.top_p ?? 1,
      stream: false, // Always false for fetch API to get JSON response
      stop: options.stop ?? null
    };
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${trimmedApiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });
    
    // Parse JSON response
    if (!response.ok) {
      let errorData;
      try {
        const raw = await response.text();
        errorData = JSON.parse(raw);
        const errorMessage = errorData.error?.message || errorData.error?.code || response.statusText;
        throw new Error(`Groq API error: ${errorMessage}`);
      } catch (e) {
        // If it's already our error, re-throw it
        if (e.message && e.message.startsWith('Groq API error:')) {
          throw e;
        }
        // Otherwise, it's a JSON parse error or network error
        console.error('Groq error response:', errorData || await response.text());
        throw new Error(`Groq API error: ${response.statusText || 'Unknown error'}`);
      }
    }
    
    // Parse successful JSON response
    let data;
    try {
      data = await response.json(); // Use response.json() instead of text() + JSON.parse()
    } catch (e) {
      console.error('Groq JSON parse error:', e);
      const rawText = await response.text();
      console.error('Groq raw response:', rawText);
      throw new Error('Groq returned invalid JSON: ' + rawText.substring(0, 200));
    }
    
    // Validate response structure
    if (!data || !data.choices || !Array.isArray(data.choices) || data.choices.length === 0) {
      console.error('Groq invalid response structure:', data);
      throw new Error('Invalid or empty response from Groq: ' + JSON.stringify(data));
    }
    
    const content = data.choices[0]?.message?.content;
    if (!content) {
      console.error('Groq empty content in response:', data);
      throw new Error('Empty content in Groq response: ' + JSON.stringify(data));
    }
    
    return content;
  }
};

export const generateContent = async (messages, options = {}) => {
  const openrouterApiKey = import.meta.env.VITE_OPENROUTER_API_KEY;
  const openrouterApiUrl = import.meta.env.VITE_OPENROUTER_URL;
  const groqApiKey = import.meta.env.VITE_GROQ_API_KEY;
  const groqApiUrl = import.meta.env.VITE_GROQ_API_URL;
  const errors = [];

  // Try OpenRouter first if configured
  if (openrouterApiKey && openrouterApiUrl) {
    try {
      return await generateWithOpenRouter(messages, options);
    } catch (err) {
      errors.push(`OpenRouter: ${err.message}`);
      console.warn('OpenRouter failed, trying next provider:', err.message);
    }
  }
  
  // Try Groq if configured
  if (groqApiKey && groqApiUrl) {
    try {
      return await generateWithGroq(messages, options);
    } catch (err) {
      errors.push(`Groq: ${err.message}`);
      console.warn('Groq failed:', err.message);
    }
  }
  
  // If all fail or none are configured
  if (errors.length > 0) {
    throw new Error(`All AI providers failed:\n${errors.join('\n')}\n\nPlease check your API keys and configuration.`);
  }
  throw new Error('No AI backend is configured. Please set VITE_OPENROUTER_API_KEY and VITE_OPENROUTER_URL or VITE_GROQ_API_KEY and VITE_GROQ_API_URL.');
};

